import React, { useState, useEffect } from 'react';
import { X, Building2, Globe, Palette } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

export default function EditOrganizationModal({ isOpen, onClose, onSave, organization }) {
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    industryId: '',
    logoUrl: '',
    primaryColor: '#3B82F6',
    secondaryColor: '#8B5CF6'
  });
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && organization) {
      setFormData({
        name: organization?.name || '',
        subdomain: organization?.subdomain || '',
        industryId: organization?.industryId || '',
        logoUrl: organization?.logoUrl || '',
        primaryColor: organization?.primaryColor || '#3B82F6',
        secondaryColor: organization?.secondaryColor || '#8B5CF6'
      });
      loadIndustries();
    }
  }, [isOpen, organization]);

  const loadIndustries = async () => {
    try {
      const { data, error } = await supabase
        ?.from('company_industries')
        ?.select('id, name')
        ?.eq('is_active', true)
        ?.order('name');

      if (error) throw error;
      setIndustries(data || []);
    } catch (err) {
      console.error('Error loading industries:', err);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onSave(organization?.id, formData);
      handleClose();
    } catch (err) {
      setError(err?.message || 'Failed to update organization');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Edit Organization</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization Name *
              </label>
              <Input
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                placeholder="Acme Corporation"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subdomain *
              </label>
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-gray-400" />
                <Input
                  value={formData?.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e?.target?.value?.toLowerCase() })}
                  placeholder="acme-corp"
                  required
                />
                <span className="text-sm text-gray-500">.yourapp.com</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Industry
              </label>
              <Select
                value={formData?.industryId}
                onChange={(e) => setFormData({ ...formData, industryId: e?.target?.value })}
              >
                <option value="">Select an industry</option>
                {industries?.map((industry) => (
                  <option key={industry?.id} value={industry?.id}>
                    {industry?.name}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo URL (optional)
              </label>
              <Input
                value={formData?.logoUrl}
                onChange={(e) => setFormData({ ...formData, logoUrl: e?.target?.value })}
                placeholder="https://example.com/logo.png"
                type="url"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Palette className="w-4 h-4 inline mr-1" />
                  Primary Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData?.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e?.target?.value })}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    value={formData?.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e?.target?.value })}
                    placeholder="#3B82F6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Palette className="w-4 h-4 inline mr-1" />
                  Secondary Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData?.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e?.target?.value })}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    value={formData?.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e?.target?.value })}
                    placeholder="#8B5CF6"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}